import { FC, useEffect, useState } from 'react';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import type { AdminLevel } from '@/types/admin';

const AdminLevelsPage: FC = () => {
  const { showToast } = useToast();
  const [levels, setLevels] = useState<AdminLevel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    name: string;
    description: string;
    icon_url: string;
    xp_required: number;
  }>({ name: '', description: '', icon_url: '', xp_required: 0 });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadLevels();
  }, []);

  const loadLevels = async () => {
    try {
      const data = await adminService.getLevels();
      setLevels(data);
    } catch { /* ignore */ } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (level: AdminLevel) => {
    setEditingId(level.id);
    setEditForm({
      name: level.name,
      description: level.description,
      icon_url: level.icon_url,
      xp_required: level.xp_required,
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const saveLevel = async (id: string) => {
    setIsSaving(true);
    try {
      await adminService.updateLevel(id, editForm);
      setEditingId(null);
      await loadLevels();
    } catch {
      showToast('Erro ao salvar nível', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold">Níveis</h1>
          <p className="text-content-muted text-sm mt-1">
            Configure os níveis de progressão da plataforma
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-content-muted">Carregando...</p>
      ) : levels.length === 0 ? (
        <p className="text-content-muted">Nenhum nível configurado.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-theme-border text-left text-content-muted text-sm">
                <th className="pb-3 font-medium w-16 text-center">Nível</th>
                <th className="pb-3 font-medium">Nome</th>
                <th className="pb-3 font-medium">Descrição</th>
                <th className="pb-3 font-medium text-right">XP Necessário</th>
                <th className="pb-3 font-medium text-right w-32">Ações</th>
              </tr>
            </thead>
            <tbody>
              {levels.map((level) => (
                <tr key={level.id} className="border-b border-theme-border/50 hover:bg-surface-hover/50">
                  {editingId === level.id ? (
                    <>
                      <td className="py-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accent/20 text-accent font-bold text-sm">
                          {level.number}
                        </span>
                      </td>
                      <td className="py-3 pr-3">
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="w-full bg-surface-secondary border border-theme-border rounded-lg px-3 py-1.5 text-sm"
                        />
                      </td>
                      <td className="py-3 pr-3">
                        <input
                          type="text"
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          className="w-full bg-surface-secondary border border-theme-border rounded-lg px-3 py-1.5 text-sm"
                          placeholder="Descrição opcional"
                        />
                      </td>
                      <td className="py-3 pr-3">
                        <input
                          type="number"
                          value={editForm.xp_required}
                          onChange={(e) => setEditForm({ ...editForm, xp_required: parseInt(e.target.value) || 0 })}
                          className="w-full bg-surface-secondary border border-theme-border rounded-lg px-3 py-1.5 text-sm text-right"
                        />
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => saveLevel(level.id)}
                            disabled={isSaving}
                            className="p-2 rounded-lg hover:bg-green-500/10 text-green-400 transition-colors"
                            title="Salvar"
                          >
                            <span className="material-symbols-outlined text-xl">check</span>
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="p-2 rounded-lg hover:bg-surface-hover text-content-muted transition-colors"
                            title="Cancelar"
                          >
                            <span className="material-symbols-outlined text-xl">close</span>
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accent/20 text-accent font-bold text-sm">
                          {level.number}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          {level.icon_url && (
                            <img src={level.icon_url} alt="" className="w-6 h-6 rounded" />
                          )}
                          <span className="font-medium">{level.name}</span>
                        </div>
                      </td>
                      <td className="py-3 text-content-muted text-sm">
                        {level.description || '—'}
                      </td>
                      <td className="py-3 text-right font-mono text-sm">
                        {level.xp_required.toLocaleString('pt-BR')} XP
                      </td>
                      <td className="py-3 text-right">
                        <button
                          onClick={() => startEditing(level)}
                          className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary transition-colors"
                          title="Editar"
                        >
                          <span className="material-symbols-outlined text-xl">edit</span>
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminLevelsPage;
